import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ExternalLink } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { GenericLoading } from 'components/ui/Loader';
import { NsKey } from 'enums/NsKey';
import { CorporateBankAccountsManager } from 'back-office/features/plateformes-view/detail/CorporateBankAccountsManager';
import {
  PersonneMoraleListItemDto,
  getPersonneMorale
} from 'back-office/features/personnes-morales-view/api/personnesMoralesApi';

import { PersonneMoraleProfileHeader } from './PersonneMoraleProfileHeader';
import { PersonneMoraleProfileTabs } from './PersonneMoraleProfileTabs';
import { PersonneMoraleInfoCard } from './PersonneMoraleInfoCard';
import { profileFromPersonneMorale } from './personneMoraleProfile';

interface PersonneMoraleQuickViewProps {
  personneMoraleId: string;
  /** Label above the name (default "Intermédiaire" — plateforme context). */
  overline?: string;
  /** Closes the host modal before navigating to the full details page. */
  onClose?: () => void;
}

/**
 * Quick view of a personne morale, in the same layout as the Gouvernance tab
 * (hero + Identity / Collaborators) but read-only: identity, contact, bank
 * accounts and members, without leaving the current screen. A link opens the
 * full details page for edition.
 */
export const PersonneMoraleQuickView: React.FC<
  PersonneMoraleQuickViewProps
> = ({ personneMoraleId, overline, onClose }) => {
  const { t } = useTranslation(NsKey.PersonneMorale);
  const navigate = useNavigate();
  const [pm, setPm] = useState<PersonneMoraleListItemDto | null>(null);
  const [error, setError] = useState(false);
  const [membersCount, setMembersCount] = useState<number | null>(null);

  useEffect(() => {
    let cancelled = false;
    getPersonneMorale(personneMoraleId)
      .then(data => {
        if (cancelled) return;
        setPm(data);
        setMembersCount(data.collaboratorsCount ?? null);
      })
      .catch(() => {
        if (!cancelled) setError(true);
      });
    return () => {
      cancelled = true;
    };
  }, [personneMoraleId]);

  if (error) {
    return (
      <div className="rounded-xl border border-dashed border-gray-200 p-8 text-center text-sm text-gray-500">
        {t('profile.quickViewError')}
      </div>
    );
  }

  if (!pm) {
    return <GenericLoading isPageCentered loading />;
  }

  const profile = profileFromPersonneMorale(pm);

  // w-[64rem] (= the dialog's max-w-5xl): without a forced width the dialog
  // panel fits its content and the modal resizes between tabs.
  return (
    <div className="max-h-[80vh] w-[64rem] max-w-full overflow-y-auto p-1">
      {/* Same card as the Gouvernance tab: the hero is the card header
          (edge-to-edge via the variant wrapper), the tabs are the body. */}
      <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
        <div className="[&_.pm-profile-hero]:rounded-none">
          <PersonneMoraleProfileHeader
            profile={profile}
            overline={overline ?? t('profile.intermediary')}
            stats={[
              { label: t('profile.stats.orias'), value: profile.orias },
              { label: 'SIREN / SIRET', value: profile.siren },
              {
                label: t('profile.stats.collaborators'),
                value: membersCount ?? undefined
              }
            ]}
            actions={
              <button
                type="button"
                onClick={() => {
                  onClose?.();
                  navigate(
                    `/back-office/acteurs/personnes-morales/${personneMoraleId}`
                  );
                }}
                className="inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-medium text-primary hover:bg-primary/5"
              >
                {t('profile.openFullProfile')}
                <ExternalLink size={14} />
              </button>
            }
          />
        </div>

        <div className="px-6 pb-6 pt-4 shadow-inner">
          <PersonneMoraleProfileTabs
            personneMoraleId={personneMoraleId}
            membersCount={membersCount}
            onMembersCountChange={setMembersCount}
            leftColumn={
              <>
                <PersonneMoraleInfoCard profile={profile} variant="identity" />
                <CorporateBankAccountsManager personId={personneMoraleId} />
              </>
            }
            rightColumn={
              <PersonneMoraleInfoCard profile={profile} variant="contact" />
            }
          />
        </div>
      </div>
    </div>
  );
};
